import React, { useState, useEffect, useRef } from 'react';
import SectionAgent from './SectionAgent';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Bell, BellRing, Plus, Mic, MicOff, Check, Trash2, X,
  Clock, Repeat, AlertCircle, CheckCircle, Filter, Search
} from 'lucide-react';
import AIAgentModule from './AIAgentModule';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const REMINDER_CATEGORIES = [
  { id: 'Todos', label: 'Todos', emoji: '📋' },
  { id: 'General', label: 'General', emoji: '🔔' },
  { id: 'Personal', label: 'Personal', emoji: '👤' },
  { id: 'Trabajo', label: 'Trabajo', emoji: '💼' },
  { id: 'Salud', label: 'Salud', emoji: '❤️' },
  { id: 'Pagos', label: 'Pagos', emoji: '💳' },
  { id: 'Familia', label: 'Familia', emoji: '👨‍👩‍👧' },
  { id: 'Ejercicio', label: 'Ejercicio', emoji: '🏋️' },
];

const PRIORITIES = [
  { id: 'low', label: 'Baja', color: 'text-gray-400', bg: 'bg-gray-500/20' },
  { id: 'normal', label: 'Normal', color: 'text-blue-400', bg: 'bg-blue-500/20' },
  { id: 'high', label: 'Alta', color: 'text-orange-400', bg: 'bg-orange-500/20' },
  { id: 'urgent', label: 'Urgente', color: 'text-red-400', bg: 'bg-red-500/20' },
];

const RemindersModule = ({ user }) => {
  const [reminders, setReminders] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('Todos');
  const [filter, setFilter] = useState('all'); // all | pending | done
  const [search, setSearch] = useState('');
  const [listening, setListening] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [newReminder, setNewReminder] = useState({
    title: '', datetime: '', category: 'General', priority: 'normal', repeat: 'none', notes: ''
  });
  const recognitionRef = useRef(null);
  const checkerRef = useRef(null);

  useEffect(() => {
    loadReminders();
    checkNotifications();
    setupRecognition();
    setupChecker();
    return () => clearInterval(checkerRef.current);
  }, [user.id]);

  const setupRecognition = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const r = new SR();
    r.lang = 'es-MX';
    r.onresult = (e) => {
      setNewReminder(p => ({ ...p, title: e.results[0][0].transcript }));
      setListening(false);
    };
    r.onerror = r.onend = () => setListening(false);
    recognitionRef.current = r;
  };

  const setupChecker = () => {
    checkerRef.current = setInterval(() => {
      const now = new Date();
      setReminders(prev => {
        prev.forEach(r => {
          if (!r.completed && r.datetime) {
            const diff = new Date(r.datetime) - now;
            if (diff > 0 && diff < 60000 && Notification.permission === 'granted') {
              new Notification(`🔔 Recordatorio: ${r.title}`, { body: `Categoría: ${r.category}` });
            }
          }
        });
        return prev;
      });
    }, 30000);
  };

  const checkNotifications = async () => {
    if ('Notification' in window) {
      setNotificationsEnabled(Notification.permission === 'granted');
    }
  };

  const requestNotifications = async () => {
    if (!('Notification' in window)) { toast.error('Tu navegador no soporta notificaciones'); return; }
    const result = await Notification.requestPermission();
    setNotificationsEnabled(result === 'granted');
    if (result === 'granted') toast.success('Notificaciones activadas');
    else toast.error('Notificaciones denegadas');
  };

  const loadReminders = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reminders/${user.id}`);
      setReminders(res.data || []);
    } catch (e) {}
    finally { setLoading(false); }
  };

  const handleVoice = () => {
    if (!recognitionRef.current) { toast.error('Voz no disponible'); return; }
    if (listening) { recognitionRef.current.stop(); return; }
    recognitionRef.current.start();
    setListening(true);
    setShowCreate(true);
  };

  const createReminder = async () => {
    if (!newReminder.title.trim()) { toast.error('Agrega un título'); return; }
    try {
      await axios.post(`${API}/reminders`, { ...newReminder, user_id: user.id, is_voice: false });
      toast.success('Recordatorio creado');
      setNewReminder({ title: '', datetime: '', category: 'General', priority: 'normal', repeat: 'none', notes: '' });
      setShowCreate(false);
      loadReminders();
    } catch (e) { toast.error('Error al crear'); }
  };

  const toggleComplete = async (r) => {
    try {
      await axios.put(`${API}/reminders/${r.id}`, { ...r, completed: !r.completed });
      setReminders(prev => prev.map(rem => rem.id === r.id ? { ...rem, completed: !rem.completed } : rem));
    } catch (e) { toast.error('Error al actualizar'); }
  };

  const deleteReminder = async (id) => {
    if (!window.confirm('¿Eliminar este recordatorio?')) return;
    await axios.delete(`${API}/reminders/${id}`);
    setReminders(prev => prev.filter(r => r.id !== id));
    toast.success('Recordatorio eliminado');
  };

  const getIsOverdue = (r) => !r.completed && r.datetime && new Date(r.datetime) < new Date();

  const filtered = reminders
    .filter(r => selectedCategory === 'Todos' || r.category === selectedCategory)
    .filter(r => filter === 'all' || (filter === 'pending' ? !r.completed : r.completed))
    .filter(r => !search || r.title?.toLowerCase().includes(search.toLowerCase()));

  const pending = reminders.filter(r => !r.completed).length;
  const overdue = reminders.filter(r => getIsOverdue(r)).length;

  return (
      <>
      <SectionAgent section="reminders" />
      <div className="p-4 md:p-6 min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Bell className="text-orange-400" size={24} />
            Recordatorios
          </h1>
          <div className="flex items-center gap-3 mt-0.5">
            <span className="text-gray-500 text-sm">{pending} pendiente{pending !== 1 ? 's' : ''}</span>
            {overdue > 0 && <span className="text-red-400 text-sm font-medium flex items-center gap-1"><AlertCircle size={12} />{overdue} vencido{overdue !== 1 ? 's' : ''}</span>}
          </div>
        </div>
        <div className="flex gap-2">
          {!notificationsEnabled && (
            <button onClick={requestNotifications} className="btn-secondary flex items-center gap-2 text-sm">
              <BellRing size={16} />Activar notificaciones
            </button>
          )}
          <button onClick={handleVoice}
            className={`p-2.5 rounded-xl transition-colors ${listening ? 'bg-red-500 text-white animate-pulse' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}
            title="Crear por voz">
            {listening ? <MicOff size={20} /> : <Mic size={20} />}
          </button>
          <button onClick={() => setShowCreate(true)} className="btn-primary flex items-center gap-2">
            <Plus size={18} />Nuevo
          </button>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Total', value: reminders.length, color: 'text-gray-300' },
          { label: 'Pendientes', value: pending, color: 'text-blue-400' },
          { label: 'Vencidos', value: overdue, color: 'text-red-400' },
          { label: 'Completados', value: reminders.filter(r => r.completed).length, color: 'text-green-400' },
        ].map(s => (
          <div key={s.label} className="glass-panel p-3 text-center rounded-xl">
            <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
            <div className="text-gray-500 text-xs mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Category Tabs */}
      <div className="flex gap-2 mb-4 overflow-x-auto pb-2 no-scrollbar">
        {REMINDER_CATEGORIES.map(cat => (
          <button key={cat.id} onClick={() => setSelectedCategory(cat.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-all ${
              selectedCategory === cat.id ? 'bg-red-500 text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}>
            {cat.emoji} {cat.label}
          </button>
        ))}
      </div>

      {/* Filter + Search row */}
      <div className="flex gap-3 mb-6">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar recordatorios..." className="input-glass pl-9 w-full text-sm" />
        </div>
        <div className="flex rounded-xl overflow-hidden border border-white/10">
          {[{ id: 'all', label: 'Todos' }, { id: 'pending', label: 'Pendientes' }, { id: 'done', label: 'Hechos' }].map(f => (
            <button key={f.id} onClick={() => setFilter(f.id)}
              className={`px-3 py-2 text-xs transition-colors ${filter === f.id ? 'bg-red-500 text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Create Form */}
      {showCreate && (
        <div className="glass-panel p-5 mb-6 animate-fade-in-up">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold">Nuevo Recordatorio</h3>
            <button onClick={() => setShowCreate(false)} className="p-1 hover:bg-white/10 rounded-lg"><X size={18} className="text-gray-400" /></button>
          </div>

          <div className="flex gap-2 mb-3">
            <input value={newReminder.title} onChange={e => setNewReminder(p => ({ ...p, title: e.target.value }))}
              placeholder="¿Qué necesitas recordar?" className="input-glass flex-1" />
            <button onClick={handleVoice}
              className={`px-3 rounded-xl ${listening ? 'bg-red-500 text-white animate-pulse' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}>
              {listening ? <MicOff size={18} /> : <Mic size={18} />}
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-gray-500 text-xs mb-1 block">Fecha y hora</label>
              <input type="datetime-local" value={newReminder.datetime} onChange={e => setNewReminder(p => ({ ...p, datetime: e.target.value }))}
                className="input-glass w-full text-sm" />
            </div>
            <div>
              <label className="text-gray-500 text-xs mb-1 block">Categoría</label>
              <select value={newReminder.category} onChange={e => setNewReminder(p => ({ ...p, category: e.target.value }))} className="input-glass w-full text-sm">
                {REMINDER_CATEGORIES.filter(c => c.id !== 'Todos').map(c => <option key={c.id} value={c.id}>{c.emoji} {c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-gray-500 text-xs mb-1 block">Prioridad</label>
              <select value={newReminder.priority} onChange={e => setNewReminder(p => ({ ...p, priority: e.target.value }))} className="input-glass w-full text-sm">
                {PRIORITIES.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-gray-500 text-xs mb-1 block">Repetir</label>
              <select value={newReminder.repeat} onChange={e => setNewReminder(p => ({ ...p, repeat: e.target.value }))} className="input-glass w-full text-sm">
                <option value="none">Sin repetición</option>
                <option value="daily">Diario</option>
                <option value="weekly">Semanal</option>
                <option value="monthly">Mensual</option>
              </select>
            </div>
          </div>

          <textarea value={newReminder.notes} onChange={e => setNewReminder(p => ({ ...p, notes: e.target.value }))}
            placeholder="Notas adicionales (opcional)..." className="input-glass w-full text-sm resize-none mb-4" rows={2} />

          <div className="flex gap-3 justify-end">
            <button onClick={() => setShowCreate(false)} className="btn-secondary text-sm">Cancelar</button>
            <button onClick={createReminder} className="btn-primary text-sm flex items-center gap-2"><Check size={16} />Guardar</button>
          </div>
        </div>
      )}

      {/* Reminders List */}
      {loading ? (
        <div className="text-center py-16 text-gray-500">Cargando recordatorios...</div>
      ) : filtered.length === 0 ? (
        <div className="glass-panel p-16 text-center">
          <Bell size={48} className="text-gray-600 mx-auto mb-4" />
          <p className="text-gray-500">{search ? 'No se encontraron recordatorios' : 'Sin recordatorios en esta categoría'}</p>
          {!search && <button onClick={() => setShowCreate(true)} className="btn-primary mt-4 text-sm">Crear primer recordatorio</button>}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(r => {
            const isOverdue = getIsOverdue(r);
            const priority = PRIORITIES.find(p => p.id === r.priority) || PRIORITIES[1];
            const catEmoji = REMINDER_CATEGORIES.find(c => c.id === r.category)?.emoji || '🔔';
            return (
              <div key={r.id}
                className={`glass-panel p-4 rounded-2xl flex items-start gap-4 transition-all ${r.completed ? 'opacity-60' : ''} ${isOverdue ? 'border border-red-500/20' : ''}`}>
                {/* Complete button */}
                <button onClick={() => toggleComplete(r)} className="flex-shrink-0 mt-0.5">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${r.completed ? 'bg-green-500 border-green-500' : 'border-gray-600 hover:border-green-500'}`}>
                    {r.completed && <Check size={11} className="text-white" />}
                  </div>
                </button>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h3 className={`font-medium ${r.completed ? 'line-through text-gray-500' : 'text-white'}`}>{r.title}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${priority.bg} ${priority.color}`}>{priority.label}</span>
                    {isOverdue && <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/20 text-red-400">Vencido</span>}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500 flex-wrap">
                    {r.datetime && (
                      <span className={`flex items-center gap-1 ${isOverdue ? 'text-red-400' : ''}`}>
                        <Clock size={11} />{new Date(r.datetime).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })}
                      </span>
                    )}
                    <span>{catEmoji} {r.category}</span>
                    {r.repeat && r.repeat !== 'none' && <span className="flex items-center gap-1"><Repeat size={11} />{r.repeat}</span>}
                  </div>
                  {r.notes && <p className="text-gray-500 text-xs mt-1 truncate">{r.notes}</p>}
                </div>

                {/* Delete */}
                <button onClick={() => deleteReminder(r.id)} className="flex-shrink-0 p-1.5 hover:bg-red-500/20 rounded-lg transition-colors">
                  <Trash2 size={14} className="text-red-400" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* AI Agent */}
      <AIAgentModule sectionContext="recordatorios" compact={true} placeholder="Crea un recordatorio o pregunta algo..." />
    </div>
  
      </>
    );
};

export default RemindersModule;
